<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { fade } from 'svelte/transition';
  import PopupMap from "./PopupMap.svelte";
  import Icon from "@iconify/svelte";
  import { createMeasurementController } from "./mapMeasurementTool.js";
  import { getOrderedMapbox, resolveItemRef } from "./mapMenuOrder.js";
  import { normalizedToLeafletLatLng } from "./mapImageCoords.js";
  import { resolveImageMapLocationMenuZoom } from "./mapImageZoom.js";
  import { getDirectionsCoordsForPopup } from "./mapDirectionsHelpers.js";
  import L from "leaflet";

  export let tour;
  export let map;
  /** Leaflet image map (CRS.Simple) — use flyTo with normalized POI coords instead of Mapbox flyTo. */
  export let imageMap = false;
  /** When a location is selected (click or focus), call to highlight the building/polygon on the map. */
  export let highlightItem = () => {};
  /** When this value changes (e.g. parent increments it), clear the menu selection state. */
  export let clearTrigger = 0;
  /** When set (e.g. selection from map click), sync menu highlight to this item key. */
  export let externalSelectedKey = null;
  /** Optional: called when user selects an item (e.g. to close mobile sheet). */
  export let onItemSelect = () => {};
  /** When true (e.g. opened via Search button), focus the filter input. */
  export let focusFilterOnOpen = false;
  /** Called after focusing the filter (so parent can clear focusFilterOnOpen). */
  export let onFocusFilterDone = () => {};
  /** When true, filter is at bottom (mobile sheet); when false, filter is on top (desktop). */
  export let filterAtBottom = false;
  /** Notifies parent when Mapbox measure mode is on (to avoid empty-map click clearing selection). */
  export let onMeasurementActiveChange = () => {};

  let selectedIndex = -1;

  let measureController = null;
  let measureModeOn = false;
  let measureDistanceLabel = '—';
  let measureTooltipX = 0;
  let measureTooltipY = 0;
  let hasMeasureCursorPos = false;

  function noopMeasureMouseCleanup() {}
  let cleanupMeasureMousemove = noopMeasureMouseCleanup;

  /** Desktop locations panel only; mobile sheet uses `filterAtBottom` and omits measure. */
  $: showMeasureToolbar = Boolean(
    tour?.features?.includes('mapMeasurement') && !imageMap && map && !filterAtBottom
  );

  $: {
    cleanupMeasureMousemove();
    cleanupMeasureMousemove = noopMeasureMouseCleanup;
    if (!measureModeOn) hasMeasureCursorPos = false;
    if (showMeasureToolbar && measureModeOn && map && typeof map.getCanvasContainer === 'function') {
      const el = map.getCanvasContainer();
      const handler = (e) => {
        measureTooltipX = e.clientX;
        measureTooltipY = e.clientY;
        hasMeasureCursorPos = true;
      };
      el.addEventListener('mousemove', handler);
      cleanupMeasureMousemove = () => {
        el.removeEventListener('mousemove', handler);
        cleanupMeasureMousemove = noopMeasureMouseCleanup;
      };
    }
  }

  function destroyMeasureController() {
    if (measureController) {
      measureController.destroy();
      measureController = null;
    }
    measureModeOn = false;
    measureDistanceLabel = '—';
  }

  $: if (showMeasureToolbar && map) {
    if (!measureController) {
      measureController = createMeasurementController(map, {
        onDistanceChange: (label) => {
          measureDistanceLabel = label;
        },
        onActiveChange: (active) => {
          measureModeOn = active;
          onMeasurementActiveChange(active);
        },
      });
    }
  } else {
    destroyMeasureController();
  }

  onDestroy(() => {
    cleanupMeasureMousemove();
    destroyMeasureController();
  });

  async function toggleMeasureMode() {
    if (!measureController) return;
    if (measureController.isActive()) {
      measureController.clear();
      measureController.deactivate();
    } else {
      await measureController.activate();
    }
  }

  /** Stable key for the menu item currently selected on the map (for toggle-deselect). */
  let selectedMenuItemKey = null;
  /** Skip applying selection in focus when focus came from mousedown (click will do it). */
  let focusFromMouse = false;
  let panelElement;
  let filterText = '';
  let searchInput;
  let filterFocused = false;

  function itemKey(item) {
    if (!item) return '';
    const id = item.data?.id ?? item.data?.featureId ?? item.data?.title ?? item.title;
    return `${item.type}-${id}`;
  }
  /** Section id -> expanded (default true when section first seen) */
  let expandedBySection = {};
  $: filteredMenuSections.forEach((sec) => {
    if (expandedBySection[sec.id] === undefined) expandedBySection[sec.id] = true;
  });
  expandedBySection = expandedBySection;
  function toggleSection(sectionId) {
    expandedBySection[sectionId] = !expandedBySection[sectionId];
    expandedBySection = expandedBySection;
  }

  // Sort by title then id (order field removed; map locations order comes from mapCategories or type groups).
  function byTitleThenId(a, b) {
    const t = (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
    if (t !== 0) return t;
    return (a.id || '').localeCompare(b.id || '');
  }

  // Use mapbox with global order applied (from mapCategories when present)
  $: mapbox = tour?.mapbox ? getOrderedMapbox(tour.mapbox) : null;
  $: showMapMenuIcons = (mapbox?.showMapMenuIcons !== false);

  // When mapCategories exists, build menu from categories + uncategorized; else use fixed type groups.
  $: useSchemaCategories = (mapbox?.mapCategories?.length ?? 0) > 0;

  function toMenuItem(type, data, numericIndex) {
    const title = type === 'numericPoi' ? `${numericIndex + 1}. ${data.title}` : data.title;
    const description = type === 'building' ? data.popupText : data.description;
    return { type, title, description, data };
  }

  $: menuSections = (() => {
    if (!mapbox) return [];
    if (!useSchemaCategories) {
      // Legacy: single virtual "sections" by type (Buildings, POIs, Numbered, Areas)
      const sections = [];
      if (mapbox.buildings?.length) {
        const items = mapbox.buildings.filter((b) => !b.hidden).sort(byTitleThenId).map((b) => toMenuItem('building', b));
        sections.push({ id: 'buildings', name: 'Buildings', items });
      }
      if (mapbox.pois?.length) {
        const items = [...mapbox.pois].sort(byTitleThenId).map((p) => toMenuItem('poi', p));
        sections.push({ id: 'pois', name: 'Points of Interest', items });
      }
      if (mapbox.numericPois?.length) {
        const sorted = [...mapbox.numericPois].sort(byTitleThenId);
        const items = sorted.map((p, i) => toMenuItem('numericPoi', p, i));
        sections.push({ id: 'numericPois', name: 'Numbered Locations', items });
      }
      if (mapbox.polygonPois?.length) {
        const items = mapbox.polygonPois.filter((p) => !p.hidden).sort(byTitleThenId).map((p) => toMenuItem('polygonPoi', p));
        sections.push({ id: 'polygonPois', name: 'Areas', items });
      }
      return sections;
    }
    const categories = [...(mapbox.mapCategories ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const inCategory = new Set();
    const sections = [];
    let numericCounter = 0;
    for (const cat of categories) {
      const refs = cat.itemRefs ?? [];
      const items = [];
      for (const ref of refs) {
        const item = resolveItemRef(mapbox, ref.itemType, ref.id);
        if (!item) continue;
        inCategory.add(`${ref.itemType}-${ref.id}`);
        const title = ref.itemType === 'numericPoi' ? `${++numericCounter}. ${item.title}` : item.title;
        const description = ref.itemType === 'building' ? item.popupText : item.description;
        items.push({ type: ref.itemType, title, description, data: item });
      }
      if (items.length > 0) sections.push({ id: cat.id, name: cat.name || 'Locations', items });
    }
    const uncategorized = [];
    const includeHiddenForType = (t) => t === 'poi' || t === 'numericPoi';
    const addUncat = (type, list) => {
      if (!Array.isArray(list)) return;
      list.filter((x) => x && (includeHiddenForType(type) || !x.hidden) && !inCategory.has(`${type}-${x.id}`)).sort(byTitleThenId).forEach((item, i) => {
        const title = type === 'numericPoi' ? `${numericCounter + i + 1}. ${item.title}` : item.title;
        const description = type === 'building' ? item.popupText : item.description;
        uncategorized.push({ type, title, description, data: item });
      });
    };
    addUncat('building', mapbox.buildings);
    addUncat('poi', mapbox.pois);
    addUncat('numericPoi', mapbox.numericPois);
    addUncat('polygonPoi', mapbox.polygonPois);
    if (uncategorized.length > 0) sections.push({ id: 'uncategorized', name: 'Other', items: uncategorized });
    return sections;
  })();

  $: allItems = menuSections.flatMap((s) => s.items);

  $: filteredMenuSections = filterText.trim()
    ? menuSections.map((sec) => ({
        ...sec,
        items: sec.items.filter((item) =>
          (item.title || '').toLowerCase().includes(filterText.toLowerCase())
        ),
      })).filter((sec) => sec.items.length > 0)
    : menuSections;

  $: filteredFlattenedItems = filteredMenuSections.flatMap((s) => s.items);

  // When filter text changes, reset selection
  $: {
    filterText;
    selectedIndex = -1;
    selectedMenuItemKey = null;
  }

  // When parent requests clear (e.g. click on empty map), clear menu selection
  let prevClearTrigger = 0;
  $: if (clearTrigger !== prevClearTrigger) {
    prevClearTrigger = clearTrigger;
    selectedMenuItemKey = null;
    selectedIndex = -1;
  }

  // When selection comes from map click, sync menu highlight to that item and scroll it into view
  $: if (externalSelectedKey !== undefined && externalSelectedKey !== null) {
    selectedMenuItemKey = externalSelectedKey;
    const idx = filteredFlattenedItems.findIndex((i) => itemKey(i) === externalSelectedKey);
    selectedIndex = idx >= 0 ? idx : -1;
    if (idx >= 0) {
      tick().then(() => {
        const buttons = panelElement?.querySelectorAll('.item-button');
        buttons?.[idx]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  } else if (externalSelectedKey === null) {
    selectedMenuItemKey = null;
    selectedIndex = -1;
  }

  function focusItem(index) {
    selectedIndex = index;
    const buttons = panelElement?.querySelectorAll('.item-button');
    if (buttons?.[index]) {
      buttons[index].focus();
    }
  }

  function handleKeydown(event) {
    const popup = document.querySelector('.popup');
    if (event.key === 'Escape') {
      if (popup) {
        setTimeout(() => {
          searchInput?.focus();
        }, 0);
      }
    }
  }

  /** Leaflet image map: zoom when focusing a POI from the menu (tour mapbox override or near-max). */
  function imageMapZoomToPoiLevel(m) {
    return resolveImageMapLocationMenuZoom(
      m.getMinZoom(),
      m.getMaxZoom(),
      mapbox?.imageMapLocationMenuZoom,
    );
  }

  /** Zoom map to the item's location (building, POI, or polygon center). */
  function zoomToItem(item) {
    if (imageMap && map) {
      const z = imageMapZoomToPoiLevel(map);
      if (item.type === 'polygonPoi' && item.data.coordinates?.length >= 3) {
        const coords = item.data.coordinates;
        let nx = 0, ny = 0, n = 0;
        coords.forEach((c) => { nx += c[0]; ny += c[1]; n++; });
        if (n > 0) {
          map.flyTo(
            normalizedToLeafletLatLng(L, nx / n, ny / n, map._imageAspectWOverH ?? 1),
            z,
            { animate: true, duration: 0.35 },
          );
        }
        return;
      }
      const loc = item.data?.location;
      if (Array.isArray(loc) && loc.length >= 2) {
        map.flyTo(
          normalizedToLeafletLatLng(L, loc[0], loc[1], map._imageAspectWOverH ?? 1),
          z,
          { animate: true, duration: 0.35 },
        );
      }
      return;
    }
    if (item.type === 'building') {
      const featureId = item.data.featureId;
      if (featureId && map) {
        const features = map.querySourceFeatures('base-buildings', {
          sourceLayer: tour?.mapbox?.mapboxTilesetLayerName,
          filter: ['==', ['get', '@id'], featureId]
        });
        if (features.length > 0) {
          const coordinates = features[0].geometry.coordinates[0];
          const center = coordinates.reduce((acc, coord) => {
            return [acc[0] + coord[0], acc[1] + coord[1]];
          }, [0, 0]).map(sum => sum / coordinates.length);
          map.flyTo({ center: center, zoom: 18 });
        }
      }
    } else if (item.type === 'polygonPoi' && item.data.coordinates?.length >= 3) {
      const coords = item.data.coordinates;
      let sumLng = 0, sumLat = 0, count = 0;
      coords.forEach(coord => {
        sumLng += coord[0];
        sumLat += coord[1];
        count++;
      });
      if (count > 0) {
        map.flyTo({
          center: [sumLng / count, sumLat / count],
          zoom: 16,
        });
      }
    } else if (item.data.location && map) {
      map.flyTo({
        center: item.data.location,
        zoom: 18,
      });
    }
  }

  /** Open popup for the item (current accessibility behavior). */
  function openPopupForItem(item) {
    const popup = {
      title: item.title,
      content: item.description,
      linkedscenes: item.data.sceneIds?.map(sceneId => {
        const scene = tour?.scenes?.find(scene => scene.id === sceneId);
        return {
          id: sceneId,
          name: scene?.name,
          title: scene?.title
        };
      }),
      directions: getDirectionsCoordsForPopup(item.data, tour?.mapbox?.mapDisplayMode)
    };
    if (popup.title || popup.content || popup.linkedscenes?.length) {
      new PopupMap({
        target: document.body,
        props: popup
      });
    }
  }

  /**
   * Click: zoom + highlight, or deselect if same item clicked again (toggle).
   * On mobile (filterAtBottom), re-selecting the same item still goes to the location.
   * Keyboard (Enter/Space): open popup (accessibility behavior).
   */
  function handleItemSelect(item, event) {
    const isKeyboard = event && event.detail === 0;
    if (isKeyboard) {
      openPopupForItem(item);
      onItemSelect();
      return;
    }
    const key = itemKey(item);
    if (!filterAtBottom && key && key === selectedMenuItemKey) {
      selectedMenuItemKey = null;
      highlightItem(null);
      return;
    }
    selectedMenuItemKey = key;
    zoomToItem(item);
    highlightItem(item);
    onItemSelect();
  }

  function handleItemFocus(item) {
    if (focusFromMouse) {
      focusFromMouse = false;
      return;
    }
    selectedMenuItemKey = itemKey(item);
    zoomToItem(item);
    highlightItem(item);
  }

  const ICON_SIZE = 18;

  /** POI icon: Iconify id (e.g. "mdi:map-marker") or legacy name (e.g. "accessibility-1", "pin"). */
  const LEGACY_POI_ICONS = ['accessibility-1', 'accessibility-2', 'accessibility-3', 'accessibility-4', 'pin'];

  function isIconifyId(icon) {
    return icon && String(icon).includes(':');
  }

  function getPoiIconUrl(icon, menuGrayHex = '666666') {
    if (!icon) return null;
    if (isIconifyId(icon)) {
      const [prefix, name] = icon.split(':');
      if (!prefix || !name) return null;
      return `https://api.iconify.design/${prefix}/${name}.svg?color=%23${menuGrayHex}`;
    }
    if (LEGACY_POI_ICONS.includes(icon)) {
      return `./images/accessibility/${icon}.svg`;
    }
    return null;
  }

  let prevFocusFilterOnOpen = false;
  $: if (focusFilterOnOpen && !prevFocusFilterOnOpen) {
    prevFocusFilterOnOpen = true;
    tick().then(() => {
      searchInput?.focus();
      onFocusFilterDone();
    });
  } else if (!focusFilterOnOpen) {
    prevFocusFilterOnOpen = false;
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

<div class="maplocations-root">
  {#if showMeasureToolbar}
    <div class="map-measure-floating" role="region" aria-label="Distance measurement">
      <button
        type="button"
        class="map-measure-btn"
        class:active={measureModeOn}
        aria-pressed={measureModeOn}
        on:click={toggleMeasureMode}
      >
        <span class="map-measure-btn-icon" aria-hidden="true">
          <Icon icon="mdi:ruler" width={20} height={20} />
        </span>
        <span class="map-measure-btn-label">Measure</span>
      </button>
    </div>
    {#if measureModeOn && hasMeasureCursorPos}
      <div
        class="map-measure-cursor-tooltip"
        style="left: {measureTooltipX + 16}px; top: {measureTooltipY + 16}px;"
        role="status"
        aria-live="polite"
        aria-label={`Measured distance ${measureDistanceLabel}`}
      >
        {measureDistanceLabel}
      </div>
    {/if}
  {/if}
<div class="maplocations-outer" bind:this={panelElement} on:wheel|stopPropagation>
  <div
    class="maplocations-panel"
    class:filter-at-bottom={filterAtBottom}
    role="region"
    aria-label="Map locations"
  >
  <div 
    class="content"
    class:content-empty={filteredFlattenedItems.length === 0}
    role="application"
    aria-label="Locations list"
  >
    {#each filteredMenuSections as section (section.id)}
      <div 
        class="group"
        role="region"
        aria-labelledby="section-heading-{section.id}"
      >
        <button
          id="section-heading-{section.id}"
          class="group-heading"
          class:expanded={expandedBySection[section.id]}
          on:click={() => toggleSection(section.id)}
          aria-expanded={expandedBySection[section.id]}
          aria-controls="section-content-{section.id}"
        >
          <span class="group-heading-chevron" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M3 4.5L6 7.5 9 4.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="group-heading-label">{section.name}</span>
        </button>
        <div
          id="section-content-{section.id}"
          class="group-content"
          class:collapsed={!expandedBySection[section.id]}
          role="listbox"
          aria-labelledby="section-heading-{section.id}"
        >
          {#each section.items as item}
            {@const isMapSelected = itemKey(item) === selectedMenuItemKey}
            <button
              class="item-button"
              class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              class:map-selected={isMapSelected}
              on:mousedown={() => (focusFromMouse = true)}
              on:click={(e) => handleItemSelect(item, e)}
              on:focus={() => handleItemFocus(item)}
              aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              aria-description={isMapSelected && !filterAtBottom ? 'Click again to clear map selection' : ''}
              role="option"
            >
              {#if showMapMenuIcons}
                <span class="item-icon item-icon-{item.type}" aria-hidden="true">
                  <!-- building: use map icon when set, else default building SVG -->
                  {#if item.type === 'building'}
                    {@const buildingIconUrl = getPoiIconUrl(item.data?.icon)}
                    {@const isLegacyBuildingIcon = item.data?.icon && LEGACY_POI_ICONS.includes(item.data.icon)}
                    {#if buildingIconUrl}
                      <img class="menu-poi-icon-img" class:menu-poi-icon-legacy={isLegacyBuildingIcon} src={buildingIconUrl} width={ICON_SIZE} height={ICON_SIZE} alt="" />
                    {:else}
                      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"/></svg>
                    {/if}
                  <!-- poi: use map icon when set, else generic pin; style to match menu -->
                  {:else if item.type === 'poi'}
                    {@const poiIconUrl = getPoiIconUrl(item.data?.icon)}
                    {@const isLegacyPoiIcon = item.data?.icon && LEGACY_POI_ICONS.includes(item.data.icon)}
                    {#if poiIconUrl}
                      <img class="menu-poi-icon-img" class:menu-poi-icon-legacy={isLegacyPoiIcon} src={poiIconUrl} width={ICON_SIZE} height={ICON_SIZE} alt="" />
                    {:else}
                      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {/if}
                  <!-- numericPoi / number -->
                  {:else if item.type === 'numericPoi'}
                    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h4v4H4zM16 4h4v4h-4zM4 16h4v4H4zM16 16h4v4h-4zM10 6h4M10 18h4M6 10v4M18 10v4"/></svg>
                  <!-- polygonPoi / area: same icon rules as POI (Iconify / legacy), else default area glyph -->
                  {:else if item.type === 'polygonPoi'}
                    {@const polyMenuIconUrl = getPoiIconUrl(item.data?.icon)}
                    {@const isLegacyPolyMenuIcon = item.data?.icon && LEGACY_POI_ICONS.includes(item.data.icon)}
                    {#if polyMenuIconUrl}
                      <img class="menu-poi-icon-img" class:menu-poi-icon-legacy={isLegacyPolyMenuIcon} src={polyMenuIconUrl} width={ICON_SIZE} height={ICON_SIZE} alt="" />
                    {:else}
                      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7v10h4v-6h4v6h4V7L12 2z"/></svg>
                    {/if}
                  {:else}
                    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7v10h4v-6h4v6h4V7L12 2z"/></svg>
                  {/if}
                </span>
              {:else}
                <span class="item-icon" aria-hidden="true" style="width: {ICON_SIZE}px; height: {ICON_SIZE}px;"></span>
              {/if}
              <span class="item-title">{item.title}</span>
              {#if isMapSelected && !filterAtBottom}
                <span class="item-deselect" aria-label="Clear selection (click to deselect)" title="Click to clear selection">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </span>
              {/if}
            </button>
          {/each}
        </div>
      </div>
    {/each}

    {#if filteredFlattenedItems.length === 0}
      <div class="no-results" transition:fade={{ duration: 200 }}>
        {#if filterText.trim()}
          <p class="no-results-message">No locations match "<span class="no-results-query">{filterText.trim()}</span>"</p>
          <p class="no-results-hint">Try different words or clear the search to see all locations.</p>
          <button type="button" class="no-results-clear" on:click={() => (filterText = '')} aria-label="Clear search to show all locations">
            Clear search
          </button>
        {:else}
          <p class="no-results-message">No locations to show</p>
        {/if}
      </div>
    {/if}
  </div>
  <div class="filter">
    <label class="filter-wrap">
      <span class="filter-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
      </span>
      <input
        type="text"
        placeholder="Search Locations"
        bind:value={filterText}
        bind:this={searchInput}
        aria-label="Search locations"
        class="filter-input"
        class:has-clear={filterText.trim()}
        on:focus={() => (filterFocused = true)}
        on:blur={() => (filterFocused = false)}
      >
      {#if filterText.trim()}
        <button type="button" class="filter-clear" on:click|stopPropagation={() => (filterText = '')} aria-label="Clear search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>
        </button>
      {/if}
    </label>
    {#if filterAtBottom && filterFocused}
      <button type="button" class="filter-dismiss" on:click={() => searchInput?.blur()} aria-label="Close keyboard" transition:fade={{ duration: 160 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
      </button>
    {/if}
  </div>
  </div>
</div>
</div>

<style>
  .maplocations-root {
    display: contents;
  }

  .map-measure-floating {
    position: fixed;
    top: 20px;
    left: 20px;
    z-index: 1100;
    display: flex;
    flex-direction: column;
    gap: 6px;
    pointer-events: auto;
  }

  .map-measure-cursor-tooltip {
    position: fixed;
    z-index: 1200;
    margin: 0;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 600;
    color: #0d47a1;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid #90caf9;
    border-radius: 6px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    white-space: nowrap;
    transform: translate(0, 0);
  }

  .maplocations-outer {
    position: absolute;
    top: 90px;
    right: 20px;
    width: 300px;
    max-height: calc(100vh - 140px);
    z-index: 1100;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .maplocations-panel {
    position: relative;
    flex: 1;
    min-height: 0;
    width: 100%;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .maplocations-panel .filter {
    order: 0;
  }
  .maplocations-panel .content {
    order: 1;
  }
  .maplocations-panel.filter-at-bottom .filter {
    order: 1;
  }
  .maplocations-panel.filter-at-bottom .content {
    order: 0;
  }

  .map-measure-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: auto;
    min-width: 120px;
    margin: 0;
    padding: 10px 14px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    cursor: pointer;
  }

  .map-measure-btn:hover {
    background: #f8f8f8;
    border-color: #ccc;
  }

  .map-measure-btn.active {
    border-color: #2196f3;
    background: #e3f2fd;
    color: #0d47a1;
  }

  .map-measure-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--focus-color, #2196f3);
  }

  .map-measure-btn-icon {
    display: inline-flex;
    color: #555;
  }

  .map-measure-btn.active .map-measure-btn-icon {
    color: #1565c0;
  }

  .content {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .content.content-empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .group {
    border-bottom: 2px solid #eee;
  }

  .group:last-child {
    border-bottom: none;
  }

  .group-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin: 0;
    padding: 12px 15px;
    background: #f8f8f8;
    font-size: 1.1em;
    color: #444;
    font-weight: 600;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
  }

  .group-heading:hover {
    background: #eee;
  }

  .group-heading:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--focus-color, #2196f3);
  }

  .group-heading-chevron {
    display: inline-flex;
    transition: transform 0.22s ease;
    color: #666;
  }

  .group-heading.expanded .group-heading-chevron {
    transform: rotate(0deg);
  }

  .group-heading:not(.expanded) .group-heading-chevron {
    transform: rotate(-90deg);
  }

  .group-heading-label {
    flex: 1;
  }

  .group-content {
    overflow: hidden;
    transition: max-height 0.28s ease-out;
  }

  .group-content.collapsed {
    max-height: 0;
  }

  .group-content:not(.collapsed) {
    max-height: 2000px;
  }

  .item-button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 10px 15px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    color: #333;
    font-size: 14px;
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  .item-icon {
    flex-shrink: 0;
    color: #666;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  /* POI icons from map (legacy SVG / Iconify) styled to match menu */
  .menu-poi-icon-img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    display: block;
  }

  /* Legacy SVGs are theme-colored; tint to menu gray. Iconify uses color=666666 so no filter. */
  .menu-poi-icon-img.menu-poi-icon-legacy {
    filter: brightness(0) saturate(100%);
    opacity: 0.6;
  }

  .item-title {
    min-width: 0;
    flex: 1;
  }

  .item-button:last-child {
    border-bottom: none;
  }

  .item-button:hover {
    background: #f5f5f5;
  }

  .item-button:focus {
    outline: none;
  }

  .item-button:focus-visible {
    box-shadow: inset 0 0 0 4px var(--focus-color, #2196f3);
  }

  .item-button.selected {
    background: #e3f2fd;
    box-shadow: inset 0 0 0 2px #2196f3;
  }

  .item-button.map-selected {
    background: #e8f5e9;
    box-shadow: inset 0 0 0 2px #4caf50;
  }

  .item-button.map-selected:hover {
    background: #c8e6c9;
  }

  .item-deselect {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 4px;
    border-radius: 4px;
    color: #2e7d32;
  }

  .item-deselect:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #1b5e20;
  }

  .filter {
    flex-shrink: 0;
    padding: 12px 15px;
    background: #f8f9fa;
    border-top: 1px solid #eee;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .filter-wrap {
    flex: 1;
    min-width: 0;
  }

  .filter-wrap {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    width: 100%;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .filter-wrap:has(:focus-visible) {
    border-color: var(--focus-color, #2196f3);
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.15);
  }

  .filter-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    color: #757575;
    pointer-events: none;
  }

  .filter-wrap:has(:focus-visible) .filter-icon {
    color: var(--focus-color, #2196f3);
  }

  .filter-input {
    flex: 1;
    min-width: 0;
    padding: 10px 14px 10px 0;
    border: none;
    background: transparent;
    font-size: 14px;
    color: #333;
    box-sizing: border-box;
  }

  .filter-input.has-clear {
    padding-right: 8px;
  }

  .filter-input::placeholder {
    color: #9e9e9e;
  }

  .filter-input:focus {
    outline: none;
  }

  .filter-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    margin-right: 6px;
    border: none;
    background: none;
    color: #757575;
    border-radius: 50%;
    cursor: pointer;
  }

  .filter-clear:hover {
    color: #333;
    background: rgba(0, 0, 0, 0.06);
  }

  .filter-dismiss {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: #333;
    background: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
  }

  .filter-dismiss:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #000;
  }

  .no-results {
    padding: 24px 20px;
    text-align: center;
    color: #555;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .no-results-message {
    margin: 0;
    font-size: 15px;
  }

  .no-results-query {
    font-weight: 600;
    color: #333;
  }

  .no-results-hint {
    margin: 0;
    font-size: 13px;
    color: #757575;
  }

  .no-results-clear {
    margin-top: 4px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: #000;
    border: none;
    border-radius: 8px;
    cursor: pointer;
  }

  .no-results-clear:hover {
    filter: brightness(1.05);
  }
</style>
